import React from "react";
import { createPortal } from "react-dom";
import { useDraggable } from "react-draggable-hoc";
import { ReactComponent as CloseSVG } from "../../assets/close-thin.svg";

import { useThemeStyles } from "../../store/ui/ThemeProvider";
import "./popup.scss";

const popupRoot = document.getElementById("popup-root") as HTMLElement;

const ANIMATION_DURATION = 400;

interface PopupInterface {
  close: Function;
}

const PopupContext = React.createContext<PopupInterface>({ close: () => {} });

function Popup({
  children,
  className,
  title,
  close: _close,
}: {
  children: JSX.Element;
  className?: string;
  title?: string;
  close?: (...args: any) => void;
}) {
  const { popupStyles } = useThemeStyles();

  const popupRef = React.useRef() as React.RefObject<HTMLDivElement>;
  const fullScreenRef = React.useRef() as React.RefObject<HTMLButtonElement>;

  const [hasFullScreen, changeFullScreen] = React.useState(false);
  const [opened, changeOpened] = React.useState(true);

  const close = React.useCallback(() => {
    changeOpened(false);
    if (typeof _close === "function") {
      setTimeout(_close, ANIMATION_DURATION);
    }
  }, [_close]);

  useDraggable(fullScreenRef, {
    dragProps: `popup`,
    onDrag: ({ current, deltaY }: any) => {
      const wrapperNode = popupRef.current;
      if (!wrapperNode) return;

      if (Math.abs(deltaY) < 30) {
        wrapperNode.style.transition = "unset";
        wrapperNode.style.height = `calc(100% - ${current.y}px)`;
        return;
      }

      if (deltaY > 0) {
        close();
      } else {
        changeFullScreen(true);
      }
    },
  });

  return createPortal(
    <PopupContext.Provider value={{ close }}>
      <div className="backing" />
      <div
        className={["popup", !opened ? "popup-exited" : undefined]
          .filter((e) => e)
          .join(" ")}
        ref={popupRef}
        style={
          {
            ...popupStyles,
            "--duration": `${ANIMATION_DURATION}ms`,
            ...(hasFullScreen
              ? {
                  height: "100%",
                  borderRadius: 0,
                }
              : {}),
          } as React.CSSProperties
        }
      >
        <div className="popup-full-screen">
          <button
            type="button"
            ref={fullScreenRef}
            onClick={(e) => changeFullScreen(true)}
          ></button>
        </div>
        <div className="popup-header">
          <div className="title">{title}</div>
          <button type="button" className="close" onClick={close}>
            <CloseSVG />
          </button>
        </div>
        <div className={`popup-content device-settings ${className ?? ""}`}>
          {children}
        </div>
      </div>
    </PopupContext.Provider>,
    popupRoot
  );
}

export { Popup as default, PopupContext };
